import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { createComponentLogger } from "../utils/logger";

import { AppHeader } from "../components/app_header";
import { Container, Section, Title } from "../components/styled";
import { Coach as CoachModel } from "../model/Coach";
import { League as LeagueModel } from "../model/League";
import {
    Division as DivisionModel,
    Week as WeekModel,
    PlayoffStructure,
    PostseasonSeedSnapshotRow,
    MatchResult
} from "../model/Season";
import { useResource } from "../hooks/useResource";
import { useAuth } from "../hooks/useAuth";
import { SeasonSchedule } from "../components/SeasonSchedule";
import { SeasonStandings } from "../components/SeasonStandings";
import { SeasonEloTracker } from "../components/SeasonEloTracker";
import { SeasonConfiguration } from "../components/SeasonConfiguration";
import { ThisWeekMatchups } from "../components/ThisWeekMatchups";
import { PostSeasonBracket } from "../components/PostSeasonBracket";
import { PostseasonFinalizePanel } from "../components/PostseasonFinalizePanel";
import { isRegularSeasonComplete } from "../utils/seasonStandingsCore";
import { extractPlayoffStructureFromImport } from "../utils/seasonImportPlayoff";
import { parsePostseasonLockFromSeasonJson } from "../utils/postseasonFinalize";
import { parsePlayoffResultsFromSeasonJson } from "../utils/playoffResultsResolve";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { InfoIcon } from "../components/shared/InfoIcon";

const SeasonHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5em;
    flex-wrap: wrap;
    gap: 0.5em;
`;

const SeasonTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-wrap: wrap;
    min-width: 0;
`;

const SeasonTitle = styled.h1`
    margin: 0;
    font-size: 2em;
    color: #333;
    word-break: break-word;
    
    @media (max-width: 540px) {
        font-size: 1.5em;
    }
`;

const InlineEditButton = styled.button`
    background: none;
    border: none;
    color: #007bff;
    cursor: pointer;
    font-size: 0.8em;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    
    &:hover {
        background-color: #f8f9fa;
    }
`;

const InlineEditInput = styled.input`
    font-size: 2em;
    font-weight: bold;
    color: #333;
    border: 2px solid #007bff;
    border-radius: 4px;
    padding: 0.25em 0.5em;
    background-color: white;
    width: 100%;
    max-width: 400px;
    min-width: 0;
    box-sizing: border-box;
    
    @media (max-width: 540px) {
        font-size: 1.5em;
        max-width: 100%;
    }
`;

const InlineEditActions = styled.div`
    display: flex;
    gap: 0.5em;
    margin-left: 0.5em;
`;

const InlineSaveButton = styled.button`
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &:hover {
        background-color: #218838;
    }
`;

const InlineCancelButton = styled.button`
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &:hover {
        background-color: #545b62;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 1.5em;
    color: #666;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 1.5em;
    color: #dc3545;
`;

const SectionHeadingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    margin-bottom: 0.25em;
`;

const InfoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5em;
    height: 1.5em;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #6c757d;
    cursor: pointer;
    font-size: 0.9em;
    line-height: 1;

    &:hover {
        background: #e9ecef;
        color: #495057;
    }
`;

const EloDescriptionPanel = styled.div`
    margin-top: 0.75em;
    padding: 1em;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    font-size: 0.9em;
    color: #333;

    h4 {
        margin: 0 0 0.5em 0;
        font-size: 1em;
    }
    ul {
        margin: 0;
        padding-left: 1.25em;
    }
    li {
        margin-bottom: 0.25em;
    }
`;

// Export/Import styled components
const ExportImportContainer = styled.div`
    margin-top: 1em;
    padding: 1em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const ExportImportTitle = styled.h4`
    margin: 0 0 0.5em 0;
    color: #333;
`;

const ExportImportButtons = styled.div`
    display: flex;
    gap: 0.5em;
    margin-bottom: 1em;
    flex-wrap: wrap;
`;

const ExportImportButton = styled.button`
    background-color: #007bff;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9em;
    
    &:hover {
        background-color: #0056b3;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const ImportTextarea = styled.textarea`
    width: 100%;
    min-height: 150px;
    padding: 0.5em;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.8em;
    resize: vertical;
`;

const ImportActions = styled.div`
    display: flex;
    gap: 0.5em;
    margin-top: 0.5em;
`;

const SuccessMessage = styled.div`
    color: #28a745;
    font-size: 0.9em;
    margin-top: 0.5em;
`;

const ErrorMessageInline = styled.div`
    color: #dc3545;
    font-size: 0.9em;
    margin-top: 0.5em;
`;

const NotAuthorizedMessage = styled.div`
    text-align: center;
    padding: 3em 1.5em;
    color: #666;
    font-size: 1.1em;
`;

interface SeasonData {
    seasonName: string;
    divisions: DivisionModel[];
    weeks: WeekModel[];
    playoffStructure?: PlayoffStructure;
    postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
    postseasonSeededAt?: string;
    postseasonLastRegularWeekIndex?: number;
    playoffResults?: Record<string, MatchResult>;
}

interface SeasonResource {
    seasonId: string;
    leagueId: string;
    seasonData: string;
    isPublic?: boolean;
}

export const Season = () => {
    const { id } = useParams();
    const { isAuthenticated } = useAuth();
    const [editMode, setEditMode] = useState(false);
    const [seasonData, setSeasonData] = useState<SeasonData>({ 
        seasonName: '', 
        divisions: [], 
        weeks: [] 
    });
    const [originalSeasonData, setOriginalSeasonData] = useState<SeasonData>({ 
        seasonName: '', 
        divisions: [], 
        weeks: [] 
    });
    const [leagueCoaches, setLeagueCoaches] = useState<CoachModel[]>([]);
    const [leagueId, setLeagueId] = useState<string | null>(null);
    const [leagueName, setLeagueName] = useState<string>('');
    
    // Inline season name editing state
    const [isEditingSeasonName, setIsEditingSeasonName] = useState(false);
    const [editingSeasonName, setEditingSeasonName] = useState('');

    // Export/Import state
    const [showImport, setShowImport] = useState(false);
    const [importData, setImportData] = useState('');
    const [importError, setImportError] = useState('');
    const [importSuccess, setImportSuccess] = useState('');
    const [showEloInfo, setShowEloInfo] = useState(false);

    // Shared week selection so all section dropdowns stay in sync
    const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
    const hasSetDefaultWeekRef = useRef(false);

    const showPlayoffBracket = useMemo(() => {
        const ps = seasonData.playoffStructure;
        const valid =
            ps &&
            typeof ps.divisionWinnersPerDivision === 'number' &&
            typeof ps.wildcardCount === 'number' &&
            typeof ps.byeCount === 'number';
        return Boolean(valid && isRegularSeasonComplete(seasonData.weeks));
    }, [seasonData.weeks, seasonData.playoffStructure]);

    // Default to the week *after* the latest complete week (first incomplete), once when weeks first load
    useEffect(() => {
        const weeks = seasonData.weeks;
        if (!Array.isArray(weeks) || weeks.length === 0) return;
        if (hasSetDefaultWeekRef.current) return;

        const isWeekComplete = (week: WeekModel | null | undefined): boolean => {
            if (!week || !Array.isArray(week.matches) || week.matches.length === 0) return false;
            return week.matches.every(m => m && m.results != null);
        };
        const isRegularSeasonWeek = (name: string) => {
            const lower = (name || '').toLowerCase();
            return !lower.includes('preseason') && !lower.includes('postseason');
        };

        let lastComplete = -1;
        weeks.forEach((week, index) => {
            if (isRegularSeasonWeek(week?.weekName || '') && isWeekComplete(week)) lastComplete = index;
        });
        const defaultIndex = Math.min(lastComplete + 1, weeks.length - 1);
        setSelectedWeekIndex(Math.max(0, defaultIndex));
        hasSetDefaultWeekRef.current = true;
    }, [seasonData.weeks]);

    // Clamp selected week when season data loads or weeks change
    useEffect(() => {
        if (!Array.isArray(seasonData.weeks) || seasonData.weeks.length === 0) return;
        if (selectedWeekIndex < 0 || selectedWeekIndex >= seasonData.weeks.length) {
            setSelectedWeekIndex(Math.max(0, seasonData.weeks.length - 1));
        }
    }, [seasonData.weeks, selectedWeekIndex]);

    const {
        data: seasonDataRaw,
        loading: seasonLoading,
        error: seasonError,
        update: updateSeason,
    } = useResource<SeasonResource>({
        resourceName: 'seasons',
        id: id!,
        allowUnauthenticated: true, // Allow fetching public seasons without auth
    });

    // Since we're fetching by ID, we expect a single season
    let season: SeasonResource | null = Array.isArray(seasonDataRaw) ? seasonDataRaw[0] : seasonDataRaw;
    // Type guard: if season is still an array or undefined, set to null
    if (Array.isArray(season) || !season) season = null;
    
    // Check if user is authorized to view this season
    const isAuthorized = isAuthenticated || (season?.isPublic ?? false);

    // Fetch league data to get league name
    // Only fetch when we have a leagueId
    const {
        data: leagueData,
        error: leagueError,
    } = useResource<LeagueModel>({
        resourceName: 'leagues',
        id: leagueId ?? undefined,
        skipFetch: !leagueId, // Don't fetch until we have a leagueId
        allowUnauthenticated: true, // Allow fetching league for public seasons
    });

    // Fetch coaches for the league this season belongs to
    // Allow unauthenticated access for public seasons
    const {
        data: leagueCoachesData,
    } = useResource<CoachModel>({
        resourceName: 'coaches',
        parentResource: 'leagues',
        parentId: leagueId!,
        skipFetch: !leagueId,
        allowUnauthenticated: true, // Allow fetching coaches for public seasons
    });

    // Parse season data when it loads
    useEffect(() => {
        if (season && season.seasonData) {
            try {
                setLeagueId(season.leagueId);
                let parsed;
                try {
                    parsed = JSON.parse(season.seasonData);
                } catch (parseError) {
                    const logger = createComponentLogger('Season', 'parseData');
                    logger.error('Failed to parse season data JSON', { 
                        error: parseError instanceof Error ? parseError.message : String(parseError),
                        seasonData: season.seasonData?.substring(0, 100) // Log first 100 chars for debugging
                    });
                    throw new Error(`Invalid JSON in season data: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
                }
                
                // Validate parsed data structure
                if (!parsed || typeof parsed !== 'object') {
                    throw new Error('Parsed season data is not an object');
                }
                
                // Ensure required arrays exist
                const playoffStructure = extractPlayoffStructureFromImport(parsed);
                const postseasonLock = parsePostseasonLockFromSeasonJson(parsed);
                const playoffResults = parsePlayoffResultsFromSeasonJson(parsed);
                const validatedData: SeasonData = {
                    seasonName: parsed.seasonName || '',
                    divisions: Array.isArray(parsed.divisions) ? parsed.divisions : [],
                    weeks: Array.isArray(parsed.weeks) ? parsed.weeks : [],
                    ...(playoffStructure ? { playoffStructure } : {}),
                    ...postseasonLock,
                    ...(playoffResults ? { playoffResults } : {})
                };
                
                setSeasonData(validatedData);
                setOriginalSeasonData(validatedData);
            } catch (err) {
                const logger = createComponentLogger('Season', 'parseData');
                logger.error('Failed to parse season data', { error: err instanceof Error ? err.message : String(err) });
                const defaultData = { seasonName: '', divisions: [], weeks: [] };
                setSeasonData(defaultData);
                setOriginalSeasonData(defaultData);
            }
        } else if (season && !season.seasonData) {
            // Season exists but has no data
            const defaultData = { seasonName: '', divisions: [], weeks: [] };
            setSeasonData(defaultData);
            setOriginalSeasonData(defaultData);
        }
    }, [season]);

    // Set league name when league data loads
    useEffect(() => {
        if (leagueData) {
            const league = Array.isArray(leagueData) ? leagueData[0] : leagueData;
            if (league && !Array.isArray(league) && 'leagueName' in league) {
                setLeagueName(league.leagueName);
            } else {
                // Log for debugging if league data format is unexpected
                const logger = createComponentLogger('Season', 'setLeagueName');
                logger.warn('Unexpected league data format', { leagueData, league });
                setLeagueName('');
            }
        } else if (leagueError) {
            // Log error but don't break the page - league name is optional
            const logger = createComponentLogger('Season', 'fetchLeague');
            logger.warn('Failed to fetch league data', { error: leagueError });
            setLeagueName('');
        } else {
            setLeagueName('');
        }
    }, [leagueData, leagueError]);

    // Set league coaches when data loads
    useEffect(() => {
        if (leagueCoachesData) {
            if (Array.isArray(leagueCoachesData)) {
                setLeagueCoaches(leagueCoachesData);
            } else {
                const logger = createComponentLogger('Season', 'setCoaches');
                logger.warn('League coaches data is not an array', { leagueCoachesData });
                setLeagueCoaches([]);
            }
        } else {
            setLeagueCoaches([]);
        }
    }, [leagueCoachesData]);

    const handleSave = async () => {
        if (!season || !updateSeason) return;

        try {
            const updatedSeason = {
                ...season,
                seasonData: JSON.stringify(seasonData)
            };
            await updateSeason(season.seasonId, updatedSeason);
            setOriginalSeasonData(seasonData);
            setEditMode(false);
        } catch (err) {
            const logger = createComponentLogger('Season', 'updateSeason');
            logger.error('Failed to update season', { error: err instanceof Error ? err.message : String(err) });
        }
    };

    const handleCancel = () => {
        setSeasonData(originalSeasonData);
        setEditMode(false);
    };

    const handleSeasonDataChange = (newData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
        playoffStructure?: PlayoffStructure;
        postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
        postseasonSeededAt?: string;
        postseasonLastRegularWeekIndex?: number;
    }) => {
        setSeasonData({
            ...seasonData,
            ...newData
        });
    };

    const persistPostseasonLock = async (lock: {
        postseasonSeedsSnapshot: PostseasonSeedSnapshotRow[];
        postseasonSeededAt: string;
        postseasonLastRegularWeekIndex: number;
    }) => {
        const updatedSeasonData = { ...seasonData, ...lock };
        setSeasonData(updatedSeasonData);
        if (!season?.seasonId || !updateSeason) return;
        const updatedSeason = {
            ...season,
            seasonData: JSON.stringify(updatedSeasonData)
        };
        await updateSeason(season.seasonId, updatedSeason);
        setOriginalSeasonData(updatedSeasonData);
    };

    const clearPostseasonLock = async () => {
        const hasPlayoffResults =
            Boolean(seasonData.playoffResults && Object.keys(seasonData.playoffResults).length > 0);
        const msg = hasPlayoffResults
            ? 'Clear playoff seed lock? Recorded playoff game results will remain in this season.'
            : 'Clear playoff seed lock?';
        if (!window.confirm(msg)) return;
        const updatedSeasonData = { ...seasonData };
        delete updatedSeasonData.postseasonSeedsSnapshot;
        delete updatedSeasonData.postseasonSeededAt;
        delete updatedSeasonData.postseasonLastRegularWeekIndex;
        setSeasonData(updatedSeasonData);
        if (!season?.seasonId || !updateSeason) return;
        const updatedSeason = {
            ...season,
            seasonData: JSON.stringify(updatedSeasonData)
        };
        await updateSeason(season.seasonId, updatedSeason);
        setOriginalSeasonData(updatedSeasonData);
    };

    const handleResultsUpdate = async (weekIndex: number, matchIndex: number, results: any) => {
        try {
            // Validate indices
            if (!Array.isArray(seasonData.weeks) || weekIndex < 0 || weekIndex >= seasonData.weeks.length) {
                throw new Error(`Invalid week index: ${weekIndex}`);
            }
            const week = seasonData.weeks[weekIndex];
            if (!week || !Array.isArray(week.matches) || matchIndex < 0 || matchIndex >= week.matches.length) {
                throw new Error(`Invalid match index: ${matchIndex} for week ${weekIndex}`);
            }
            
            const updatedWeeks = [...seasonData.weeks];
            if (!updatedWeeks[weekIndex] || !updatedWeeks[weekIndex].matches) {
                throw new Error(`Week ${weekIndex} or its matches are invalid`);
            }
            
            updatedWeeks[weekIndex] = {
                ...updatedWeeks[weekIndex],
                matches: [...updatedWeeks[weekIndex].matches]
            };
            
            if (!updatedWeeks[weekIndex].matches[matchIndex]) {
                throw new Error(`Match ${matchIndex} in week ${weekIndex} is invalid`);
            }
            
            // Preserve existing source and createdAt if present, otherwise set to "manual" for manual edits
            const existingResults = updatedWeeks[weekIndex].matches[matchIndex].results;
            const updatedResults = {
                ...results,
                source: existingResults?.source || 'manual',
                createdAt: existingResults?.createdAt || new Date().toISOString(), // Preserve original creation time, or set if new
                updatedAt: new Date().toISOString()
            };
            
            updatedWeeks[weekIndex].matches[matchIndex] = {
                ...updatedWeeks[weekIndex].matches[matchIndex],
                results: updatedResults
            };

            const updatedSeasonData = {
                ...seasonData,
                weeks: updatedWeeks
            };

            setSeasonData(updatedSeasonData);

            // Save to backend immediately when results are entered
            if (season && updateSeason) {
                try {
                    const updatedSeason = {
                        ...season,
                        seasonData: JSON.stringify(updatedSeasonData)
                    };
                    await updateSeason(season.seasonId, updatedSeason);
                    setOriginalSeasonData(updatedSeasonData);
                } catch (err) {
                    const logger = createComponentLogger('Season', 'saveResults');
                    logger.error('Failed to save results', { error: err instanceof Error ? err.message : String(err) });
                    // Revert the local change if save failed
                    setSeasonData(originalSeasonData);
                }
            }
        } catch (err) {
            const logger = createComponentLogger('Season', 'handleResultsUpdate');
            logger.error('Error updating results', { error: err instanceof Error ? err.message : String(err), weekIndex, matchIndex });
            // Don't update state if validation fails
        }
    };

    const handlePlayoffResultsUpdate = async (matchId: string, results: MatchResult) => {
        try {
            const prevMap = seasonData.playoffResults ?? {};
            const existing = prevMap[matchId];
            const now = new Date().toISOString();
            const updatedResults: MatchResult = {
                ...results,
                source: existing?.source || results.source || 'manual',
                createdAt: existing?.createdAt || now,
                updatedAt: now
            };
            const updatedSeasonData: SeasonData = {
                ...seasonData,
                playoffResults: { ...prevMap, [matchId]: updatedResults }
            };
            setSeasonData(updatedSeasonData);
            if (season?.seasonId && updateSeason) {
                const updatedSeason = {
                    ...season,
                    seasonData: JSON.stringify(updatedSeasonData)
                };
                await updateSeason(season.seasonId, updatedSeason);
                setOriginalSeasonData(updatedSeasonData);
            }
        } catch (err) {
            const logger = createComponentLogger('Season', 'handlePlayoffResultsUpdate');
            logger.error('Error updating playoff results', { error: err instanceof Error ? err.message : String(err), matchId });
            setSeasonData(originalSeasonData);
        }
    };

    // Inline season name editing handlers
    const handleStartEditingSeasonName = () => {
        setEditingSeasonName(seasonData.seasonName);
        setIsEditingSeasonName(true);
    };

    const handleSaveSeasonName = async () => {
        const updatedSeasonData = {
            ...seasonData,
            seasonName: editingSeasonName.trim()
        };
        
        setSeasonData(updatedSeasonData);
        setIsEditingSeasonName(false);
        
        // Save to backend immediately
        if (season && updateSeason) {
            try {
                const updatedSeason = {
                    ...season,
                    seasonData: JSON.stringify(updatedSeasonData)
                };
                await updateSeason(season.seasonId, updatedSeason);
                setOriginalSeasonData(updatedSeasonData);
            } catch (err) {
                const logger = createComponentLogger('Season', 'saveSeasonName');
                logger.error('Failed to save season name', { error: err instanceof Error ? err.message : String(err) });
                // Revert the local change if save failed
                setSeasonData(originalSeasonData);
            }
        }
    };

    const handleCancelSeasonName = () => {
        setIsEditingSeasonName(false);
        setEditingSeasonName('');
    };

    // Export/Import handlers
    const handleExport = () => {
        const exportData = JSON.stringify(seasonData, null, 2);
        navigator.clipboard.writeText(exportData).then(() => {
            // Could add a toast notification here
            // Season data copied to clipboard
        }).catch(err => {
            const logger = createComponentLogger('Season', 'copyToClipboard');
            logger.error('Failed to copy to clipboard', { error: err instanceof Error ? err.message : String(err) });
        });
    };

    const handleShowImport = () => {
        setShowImport(true);
        setImportData('');
        setImportError('');
        setImportSuccess('');
    };

    const handleHideImport = () => {
        setShowImport(false);
        setImportData('');
        setImportError('');
        setImportSuccess('');
    };

    const handleImport = async () => {
        if (!importData.trim()) {
            setImportError('Please enter season data to import');
            return;
        }

        try {
            const parsedData = JSON.parse(importData);
            
            // Validate the structure
            if (!parsedData.seasonName || !Array.isArray(parsedData.divisions) || !Array.isArray(parsedData.weeks)) {
                setImportError('Invalid season data format. Expected seasonName, divisions, and weeks arrays.');
                return;
            }

            // Transform the imported data to match the expected format
            const transformedData = transformImportedData(parsedData);
            
                    // Data transformation completed

            // Update local state
            setSeasonData(transformedData);
            setImportSuccess('Season data imported successfully!');
            setImportError('');

            // Save to backend
            if (season && updateSeason) {
                try {
                    const updatedSeason = {
                        ...season,
                        seasonData: JSON.stringify(transformedData)
                    };
                    await updateSeason(season.seasonId, updatedSeason);
                    setOriginalSeasonData(transformedData);
                    setImportSuccess('Season data imported and saved successfully!');
                } catch (err) {
                    const logger = createComponentLogger('Season', 'saveImportedData');
                    logger.error('Failed to save imported data', { error: err instanceof Error ? err.message : String(err) });
                    setImportError('Data imported but failed to save to server. Please try again.');
                    // Revert the local change if save failed
                    setSeasonData(originalSeasonData);
                }
            }
        } catch (err) {
            setImportError('Invalid JSON format. Please check your data and try again.');
        }
    };

    // Transform imported data to match expected format
    const transformImportedData = (importedData: any) => {
        try {
            // Check if data is already in the new format (has divisionName, homeTeamId, etc.)
            const isNewFormat = importedData.divisions && 
                importedData.divisions.length > 0 && 
                importedData.divisions[0].divisionName !== undefined;

            if (isNewFormat) {
                // Data is already in the correct format, just validate and return it
                // Ensure all matches have proper structure (results are optional)
                const validated = {
                    seasonName: importedData.seasonName,
                    divisions: importedData.divisions.map((division: any) => ({
                        divisionName: division.divisionName,
                        teams: division.teams.map((team: any) => ({
                            teamId: team.teamId,
                            teamName: team.teamName,
                            coachId: team.coachId,
                            race: team.race
                        }))
                    })),
                    weeks: importedData.weeks.map((week: any) => ({
                        weekName: week.weekName,
                        matches: week.matches.map((match: any) => {
                            const transformedMatch: any = {
                                homeTeamId: match.homeTeamId,
                                awayTeamId: match.awayTeamId
                            };
                            
                            // Only include results if they exist and have valid data
                            // Use optional chaining to safely access properties
                            if (match.results && (match.results.homeTeamScore !== undefined || match.results.homeScore !== undefined || 
                                match.results.awayTeamScore !== undefined || match.results.awayScore !== undefined)) {
                                // Preserve all fields from the original results, with fallbacks for legacy field names
                                transformedMatch.results = {
                                    // Support both property name formats for backwards compatibility
                                    homeTeamScore: match.results?.homeTeamScore ?? match.results?.homeScore ?? 0,
                                    awayTeamScore: match.results?.awayTeamScore ?? match.results?.awayScore ?? 0,
                                    homeTeamCasInflicted: match.results?.homeTeamCasInflicted ?? match.results?.homeCasualties ?? 0,
                                    awayTeamCasInflicted: match.results?.awayTeamCasInflicted ?? match.results?.awayCasualties ?? 0,
                                    overtime: match.results?.overtime ?? false,
                                    // Preserve all additional fields if they exist
                                    ...(match.results.homeTeamKills !== undefined && { homeTeamKills: match.results.homeTeamKills }),
                                    ...(match.results.awayTeamKills !== undefined && { awayTeamKills: match.results.awayTeamKills }),
                                    ...(match.results.homeTeamTV !== undefined && { homeTeamTV: match.results.homeTeamTV }),
                                    ...(match.results.awayTeamTV !== undefined && { awayTeamTV: match.results.awayTeamTV }),
                                    ...(match.results.homeTeamBlocks !== undefined && { homeTeamBlocks: match.results.homeTeamBlocks }),
                                    ...(match.results.awayTeamBlocks !== undefined && { awayTeamBlocks: match.results.awayTeamBlocks }),
                                    ...(match.results.homeTeamAvBr !== undefined && { homeTeamAvBr: match.results.homeTeamAvBr }),
                                    ...(match.results.awayTeamAvBr !== undefined && { awayTeamAvBr: match.results.awayTeamAvBr }),
                                    ...(match.results.homeTeamKO !== undefined && { homeTeamKO: match.results.homeTeamKO }),
                                    ...(match.results.awayTeamKO !== undefined && { awayTeamKO: match.results.awayTeamKO }),
                                    ...(match.results.homeTeamSurfs !== undefined && { homeTeamSurfs: match.results.homeTeamSurfs }),
                                    ...(match.results.awayTeamSurfs !== undefined && { awayTeamSurfs: match.results.awayTeamSurfs }),
                                    ...(match.results.homeTeamFouls !== undefined && { homeTeamFouls: match.results.homeTeamFouls }),
                                    ...(match.results.awayTeamFouls !== undefined && { awayTeamFouls: match.results.awayTeamFouls }),
                                    ...(match.results.homeTeamPasses !== undefined && { homeTeamPasses: match.results.homeTeamPasses }),
                                    ...(match.results.awayTeamPasses !== undefined && { awayTeamPasses: match.results.awayTeamPasses }),
                                    ...(match.results.homeTeamSPP !== undefined && { homeTeamSPP: match.results.homeTeamSPP }),
                                    ...(match.results.awayTeamSPP !== undefined && { awayTeamSPP: match.results.awayTeamSPP }),
                                    ...(match.results.source && { source: match.results.source }),
                                    ...(match.results.createdAt && { createdAt: match.results.createdAt }),
                                    ...(match.results.updatedAt && { updatedAt: match.results.updatedAt })
                                };
                            }
                            
                            return transformedMatch;
                        })
                    }))
                };
                const playoffStructure = extractPlayoffStructureFromImport(importedData);
                const postseasonLock = parsePostseasonLockFromSeasonJson(importedData);
                const playoffResults = parsePlayoffResultsFromSeasonJson(importedData);
                const withPlayoff = playoffStructure ? { ...validated, playoffStructure } : validated;
                return { ...withPlayoff, ...postseasonLock, ...(playoffResults ? { playoffResults } : {}) };
            } else {
                // Data is in old format, transform it
                const transformed = {
                    seasonName: importedData.seasonName,
                    divisions: importedData.divisions.map((division: any) => ({
                        divisionName: division.name || division.divisionName,
                        teams: division.teams.map((team: any) => ({
                            teamId: team.teamId || `team-${team.coach}`,
                            teamName: team.teamName || team.name,
                            coachId: team.coachId || team.coach,
                            race: team.race
                        }))
                    })),
                    weeks: importedData.weeks.map((week: any) => ({
                        weekName: week.weekName || `Week ${week.weekNumber}`,
                        matches: week.matches.map((match: any) => {
                            const transformedMatch: any = {
                                homeTeamId: match.homeTeamId || `team-${match.homeTeam}`,
                                awayTeamId: match.awayTeamId || `team-${match.awayTeam}`
                            };
                            
                            // Only include results if they exist and have valid data
                            // Use optional chaining to safely access properties
                            if (match.results && (match.results.homeTeamScore !== undefined || match.results.homeScore !== undefined || 
                                match.results.awayTeamScore !== undefined || match.results.awayScore !== undefined)) {
                                // Preserve all fields from the original results, with fallbacks for legacy field names
                                transformedMatch.results = {
                                    // Support both property name formats for backwards compatibility
                                    homeTeamScore: match.results?.homeTeamScore ?? match.results?.homeScore ?? 0,
                                    awayTeamScore: match.results?.awayTeamScore ?? match.results?.awayScore ?? 0,
                                    homeTeamCasInflicted: match.results?.homeTeamCasInflicted ?? match.results?.homeCasualties ?? 0,
                                    awayTeamCasInflicted: match.results?.awayTeamCasInflicted ?? match.results?.awayCasualties ?? 0,
                                    overtime: match.results?.overtime ?? false,
                                    // Preserve all additional fields if they exist
                                    ...(match.results.homeTeamKills !== undefined && { homeTeamKills: match.results.homeTeamKills }),
                                    ...(match.results.awayTeamKills !== undefined && { awayTeamKills: match.results.awayTeamKills }),
                                    ...(match.results.homeTeamTV !== undefined && { homeTeamTV: match.results.homeTeamTV }),
                                    ...(match.results.awayTeamTV !== undefined && { awayTeamTV: match.results.awayTeamTV }),
                                    ...(match.results.homeTeamBlocks !== undefined && { homeTeamBlocks: match.results.homeTeamBlocks }),
                                    ...(match.results.awayTeamBlocks !== undefined && { awayTeamBlocks: match.results.awayTeamBlocks }),
                                    ...(match.results.homeTeamAvBr !== undefined && { homeTeamAvBr: match.results.homeTeamAvBr }),
                                    ...(match.results.awayTeamAvBr !== undefined && { awayTeamAvBr: match.results.awayTeamAvBr }),
                                    ...(match.results.homeTeamKO !== undefined && { homeTeamKO: match.results.homeTeamKO }),
                                    ...(match.results.awayTeamKO !== undefined && { awayTeamKO: match.results.awayTeamKO }),
                                    ...(match.results.homeTeamSurfs !== undefined && { homeTeamSurfs: match.results.homeTeamSurfs }),
                                    ...(match.results.awayTeamSurfs !== undefined && { awayTeamSurfs: match.results.awayTeamSurfs }),
                                    ...(match.results.homeTeamFouls !== undefined && { homeTeamFouls: match.results.homeTeamFouls }),
                                    ...(match.results.awayTeamFouls !== undefined && { awayTeamFouls: match.results.awayTeamFouls }),
                                    ...(match.results.homeTeamPasses !== undefined && { homeTeamPasses: match.results.homeTeamPasses }),
                                    ...(match.results.awayTeamPasses !== undefined && { awayTeamPasses: match.results.awayTeamPasses }),
                                    ...(match.results.homeTeamSPP !== undefined && { homeTeamSPP: match.results.homeTeamSPP }),
                                    ...(match.results.awayTeamSPP !== undefined && { awayTeamSPP: match.results.awayTeamSPP }),
                                    ...(match.results.source && { source: match.results.source }),
                                    ...(match.results.createdAt && { createdAt: match.results.createdAt }),
                                    ...(match.results.updatedAt && { updatedAt: match.results.updatedAt })
                                };
                            }
                            
                            return transformedMatch;
                        })
                    }))
                };
                const playoffStructure = extractPlayoffStructureFromImport(importedData);
                const postseasonLock = parsePostseasonLockFromSeasonJson(importedData);
                const playoffResults = parsePlayoffResultsFromSeasonJson(importedData);
                const withPlayoff = playoffStructure ? { ...transformed, playoffStructure } : transformed;
                return { ...withPlayoff, ...postseasonLock, ...(playoffResults ? { playoffResults } : {}) };
            }
        } catch (error) {
            const logger = createComponentLogger('Season', 'transformImportedData');
            logger.error('Error transforming imported data', { error: error instanceof Error ? error.message : String(error) });
            throw new Error('Failed to transform imported data structure');
        }
    };

    if (seasonLoading) {
        return (
            <Container>
                {isAuthenticated ? <AppHeader /> : (
                    <Section>
                        <Title>{leagueName ? `${leagueName} - Loading...` : 'Loading...'}</Title>
                    </Section>
                )}
                {isAuthenticated && (
                    <Section>
                        <Title>Season Details</Title>
                    </Section>
                )}
                <Section>
                    <LoadingMessage>Loading season...</LoadingMessage>
                </Section>
            </Container>
        );
    }

    if (seasonError) {
        // Check if error is due to authorization
        if (seasonError.includes('403') || seasonError.includes('Not authorized')) {
            return (
                <Container>
                    {isAuthenticated ? <AppHeader /> : (
                        <Section>
                            <Title>{leagueName ? `${leagueName} - Error` : 'Error'}</Title>
                        </Section>
                    )}
                    {isAuthenticated && (
                        <Section>
                            <Title>Season Details</Title>
                        </Section>
                    )}
                    <Section>
                        <NotAuthorizedMessage>
                            Not authorized to view this season. Please log in to access this content.
                        </NotAuthorizedMessage>
                    </Section>
                </Container>
            );
        }
        return (
            <Container>
                {isAuthenticated ? <AppHeader /> : (
                    <Section>
                        <Title>{leagueName ? `${leagueName} - Error` : 'Error'}</Title>
                    </Section>
                )}
                {isAuthenticated && (
                    <Section>
                        <Title>Season Details</Title>
                    </Section>
                )}
                <Section>
                    <ErrorMessage>Error loading season: {seasonError}</ErrorMessage>
                </Section>
            </Container>
        );
    }

    if (!season && !seasonLoading) {
        return (
            <Container>
                {isAuthenticated ? <AppHeader /> : (
                    <Section>
                        <Title>{leagueName ? `${leagueName} - Not Found` : 'Season Not Found'}</Title>
                    </Section>
                )}
                {isAuthenticated && (
                    <Section>
                        <Title>Season Details</Title>
                    </Section>
                )}
                <Section>
                    <ErrorMessage>Season not found</ErrorMessage>
                </Section>
            </Container>
        );
    }

    // Show not authorized page for logged-out users accessing private seasons
    if (season && !isAuthorized) {
        return (
            <Container>
                {isAuthenticated ? <AppHeader /> : (
                    <Section>
                        <Title>{leagueName ? `${leagueName} - Not Authorized` : 'Not Authorized'}</Title>
                    </Section>
                )}
                {isAuthenticated && (
                    <Section>
                        <Title>Season Details</Title>
                    </Section>
                )}
                <Section>
                    <NotAuthorizedMessage>
                        Not authorized to view this season. Please log in to access this content.
                    </NotAuthorizedMessage>
                </Section>
            </Container>
        );
    }

    // At this point, season must exist (all null cases have returned early)
    if (!season) {
        return null;
    }

    return (
        <Container>
            {isAuthenticated ? (
                <AppHeader />
            ) : (
                <Section>
                    <Title>
                        {leagueName && seasonData.seasonName 
                            ? `${leagueName} - ${seasonData.seasonName}`
                            : seasonData.seasonName || leagueName || 'Season'}
                    </Title>
                </Section>
            )}
            {isAuthenticated && (
                <Section>
                    <SeasonHeader>
                        <SeasonTitleContainer>
                            {isEditingSeasonName ? (
                                <>
                                    <InlineEditInput
                                        type="text"
                                        value={editingSeasonName}
                                        onChange={(e) => setEditingSeasonName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSaveSeasonName()}
                                        onKeyDown={(e) => e.key === 'Escape' && handleCancelSeasonName()}
                                        autoFocus
                                    />
                                    <InlineEditActions>
                                        <InlineSaveButton onClick={handleSaveSeasonName}>
                                            Save
                                        </InlineSaveButton>
                                        <InlineCancelButton onClick={handleCancelSeasonName}>
                                            Cancel
                                        </InlineCancelButton>
                                    </InlineEditActions>
                                </>
                            ) : (
                                <>
                                    <SeasonTitle>
                                        {seasonData.seasonName || `Season ${season.seasonId}`}
                                    </SeasonTitle>
                                    <InlineEditButton onClick={handleStartEditingSeasonName}>
                                        ✏️ Edit
                                    </InlineEditButton>
                                </>
                            )}
                        </SeasonTitleContainer>
                    </SeasonHeader>
                </Section>
            )}


            {isAuthenticated && (
                <Section>
                    <ExportImportContainer>
                        <ExportImportTitle>Export/Import Season Configuration</ExportImportTitle>
                        <p style={{ margin: '0 0 1em 0', fontSize: '0.9em', color: '#666' }}>
                            Export your season configuration to create backups, or import a previous configuration to restore it.
                        </p>
                        
                        <ExportImportButtons>
                            <ExportImportButton onClick={handleExport}>
                                📋 Export to Clipboard
                            </ExportImportButton>
                            <ExportImportButton onClick={handleShowImport}>
                                📥 Import Configuration
                            </ExportImportButton>
                        </ExportImportButtons>

                        {showImport && (
                            <div>
                                <ImportTextarea
                                    placeholder="Paste your season configuration JSON here..."
                                    value={importData}
                                    onChange={(e) => setImportData(e.target.value)}
                                />
                                <ImportActions>
                                    <ExportImportButton onClick={handleImport}>
                                        Import
                                    </ExportImportButton>
                                    <ExportImportButton 
                                        onClick={handleHideImport}
                                        style={{ backgroundColor: '#6c757d' }}
                                    >
                                        Cancel
                                    </ExportImportButton>
                                </ImportActions>
                                {importError && <ErrorMessageInline>{importError}</ErrorMessageInline>}
                                {importSuccess && <SuccessMessage>{importSuccess}</SuccessMessage>}
                            </div>
                        )}
                    </ExportImportContainer>
                </Section>
            )}

            <Section>
                <h3>{showPlayoffBracket ? 'Playoff bracket' : "This Week's Matchups"}</h3>
                <ErrorBoundary fallback={
                    <div data-testid="error" style={{ color: '#dc3545', padding: '1em' }}>
                        Error loading matchups.{isAuthenticated && ' You can still export and re-import the season data using the Export/Import section above to fix any data issues.'}
                    </div>
                }>
                    {showPlayoffBracket ? (
                        <>
                            <PostSeasonBracket
                                seasonData={seasonData}
                                leagueCoaches={leagueCoaches}
                                onPlayoffResultsUpdate={isAuthenticated ? handlePlayoffResultsUpdate : undefined}
                            />
                            {isAuthenticated && (
                                <PostseasonFinalizePanel
                                    seasonData={seasonData}
                                    leagueCoaches={leagueCoaches}
                                    visible={showPlayoffBracket}
                                    onPersist={persistPostseasonLock}
                                    onClearLock={clearPostseasonLock}
                                />
                            )}
                        </>
                    ) : (
                        <ThisWeekMatchups 
                            seasonData={seasonData}
                            leagueCoaches={leagueCoaches}
                            selectedWeekIndex={selectedWeekIndex}
                            onSelectedWeekChange={setSelectedWeekIndex}
                        />
                    )}
                </ErrorBoundary>
            </Section>

            <Section>
                <h3>Standings</h3>
                <ErrorBoundary fallback={
                    <div data-testid="error" style={{ color: '#dc3545', padding: '1em' }}>
                        Error loading standings.{isAuthenticated && ' You can still export and re-import the season data using the Export/Import section above to fix any data issues.'}
                    </div>
                }>
                    <SeasonStandings 
                        seasonData={seasonData}
                        leagueCoaches={leagueCoaches}
                        selectedWeekIndex={selectedWeekIndex}
                        onSelectedWeekChange={setSelectedWeekIndex}
                    />
                </ErrorBoundary>
            </Section>

            <Section>
                <h3>Schedule</h3>
                <ErrorBoundary fallback={
                    <div data-testid="error" style={{ color: '#dc3545', padding: '1em' }}>
                        Error loading schedule.{isAuthenticated && ' You can still export and re-import the season data using the Export/Import section above to fix any data issues.'}
                    </div>
                }>
                    <SeasonSchedule 
                        seasonData={seasonData}
                        leagueCoaches={leagueCoaches}
                        onResultsUpdate={isAuthenticated ? handleResultsUpdate : undefined}
                    />
                </ErrorBoundary>
            </Section>

            <Section>
                <SectionHeadingRow>
                    <h3 style={{ margin: 0 }}>ELO Ratings</h3>
                    <InfoButton
                        type="button"
                        onClick={() => setShowEloInfo(prev => !prev)}
                        aria-label="How ELO is calculated"
                        aria-expanded={showEloInfo}
                        title="How ELO is calculated"
                    >
                        <InfoIcon size="0.85em" />
                    </InfoButton>
                </SectionHeadingRow>
                {showEloInfo && (
                    <EloDescriptionPanel>
                        <h4>How ELO is calculated</h4>
                        <ul>
                            <li>All teams start at <strong>1200</strong> ELO.</li>
                            <li><strong>K-factor</strong> is 32 — how much each game changes your rating.</li>
                            <li><strong>Match result:</strong> Win = 1, Loss = 0, Draw = 0.5.</li>
                            <li><strong>Expected score</strong> = 1 / (1 + 10^((opponent − you) / 400)).</li>
                            <li><strong>New rating</strong> = old rating + K × (actual score − expected score).</li>
                        </ul>
                    </EloDescriptionPanel>
                )}
                <ErrorBoundary fallback={
                    <div data-testid="error" style={{ color: '#dc3545', padding: '1em' }}>
                        Error loading ELO ratings.{isAuthenticated && ' You can still export and re-import the season data using the Export/Import section above to fix any data issues.'}
                    </div>
                }>
                    <SeasonEloTracker 
                        seasonData={seasonData}
                        leagueCoaches={leagueCoaches}
                    />
                </ErrorBoundary>
            </Section>

            {isAuthenticated && (
                <Section>
                    <h3>Season Configuration</h3>
                    <ErrorBoundary fallback={
                        <div data-testid="error">
                            <p style={{ color: '#dc3545', padding: '1em' }}>
                                Error loading season configuration. You can still export and re-import the season data using the Export/Import section above to fix any data issues.
                            </p>
                            <p style={{ color: '#666', padding: '0 1em 1em 1em', fontSize: '0.9em' }}>
                                If you see this error, try exporting the season data, fixing any issues in the JSON, and re-importing it.
                            </p>
                        </div>
                    }>
                        <SeasonConfiguration 
                            seasonData={seasonData}
                            leagueCoaches={leagueCoaches}
                            editMode={editMode}
                            onEditModeChange={setEditMode}
                            onSeasonDataChange={handleSeasonDataChange}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    </ErrorBoundary>
                </Section>
            )}
        </Container>
    );
}; 